import { Injectable, OnDestroy, inject } from '@angular/core';
import { RxStomp, RxStompConfig } from '@stomp/rx-stomp';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { MessageDto } from '../interfaces/message-dto';
import Keycloak from 'keycloak-js';
import { envToken } from '../tokens/env-token';

@Injectable({ providedIn: 'root' })
export class MessagingService implements OnDestroy {
    private env = inject(envToken);
    private keycloak = inject(Keycloak);

    private rxStomp: RxStomp | null = null;
    private connectionState$ = new BehaviorSubject<'disconnected' | 'connecting' | 'connected'>('disconnected');
    private connectionSubscriptions: Subscription[] = [];
    private disconnect$ = new Subject<void>();

    connect(): boolean {
        // Guard against any existing rxStomp instance (not just connected)
        if (this.rxStomp) {
            return false;
        }

        const token = this.keycloak.token;
        if (!token) {
            console.error('No Keycloak token available');
            return false;
        }

        this.connectionState$.next('connecting');

        const config: RxStompConfig = {
            brokerURL: `${this.env.apiUrl.replace(/^http/, 'ws')}/api/v1/messages`,
            connectHeaders: {
                Authorization: `Bearer ${token}`
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 5000,
            heartbeatOutgoing: 5000
        };

        this.rxStomp = new RxStomp();
        this.rxStomp.configure(config);
        this.rxStomp.activate();

        // Fix #3: Use per-connection teardown to prevent subscription leaks
        this.connectionSubscriptions.push(
            this.rxStomp.connected$
                .pipe(takeUntil(this.disconnect$))
                .subscribe(() => this.connectionState$.next('connected'))
        );

        this.connectionSubscriptions.push(
            this.rxStomp.stompErrors$
                .pipe(takeUntil(this.disconnect$))
                .subscribe((frame) => {
                    console.error('STOMP error:', frame);
                    this.connectionState$.next('disconnected');
                })
        );

        this.connectionSubscriptions.push(
            this.rxStomp.webSocketErrors$
                .pipe(takeUntil(this.disconnect$))
                .subscribe((event) => {
                    console.error('WebSocket error:', event);
                    this.connectionState$.next('disconnected');
                })
        );

        return true;
    }

    /**
     * Watch messages sent to the current user for a specific enterprise.
     * Spring resolves the user from the authenticated session.
     * Note: connect() must be called before using this method.
     */
    watchEnterpriseMessages(enterpriseId: number): Observable<MessageDto> {
        const destination = `/user/queue/enterprise/${enterpriseId}`;

        // Fix #2: Throw meaningful error instead of race condition
        if (!this.rxStomp) {
            throw new Error('MessagingService: connect() must be called before watching messages');
        }

        return this.rxStomp.watch(destination).pipe(
            map((message) => JSON.parse(message.body) as MessageDto),
            filter((msg): msg is MessageDto => msg !== null)
        );
    }

    /**
     * Watch messages broadcast to all users of an enterprise.
     * Note: connect() must be called before using this method.
     */
    watchEnterpriseBroadcast(enterpriseId: number): Observable<MessageDto> {
        const destination = `/queue/enterprise/${enterpriseId}`;

        if (!this.rxStomp) {
            throw new Error('MessagingService: connect() must be called before watching messages');
        }

        return this.rxStomp.watch(destination).pipe(
            map((message) => JSON.parse(message.body) as MessageDto),
            filter((msg): msg is MessageDto => msg !== null)
        );
    }

    /**
     * Watch global messages broadcast to all connected users.
     * Note: connect() must be called before using this method.
     */
    watchGlobalMessages(): Observable<MessageDto> {
        const destination = `/queue/global`;

        if (!this.rxStomp) {
            throw new Error('MessagingService: connect() must be called before watching messages');
        }

        return this.rxStomp.watch(destination).pipe(
            map((message) => JSON.parse(message.body) as MessageDto),
            filter((msg): msg is MessageDto => msg !== null)
        );
    }

    get connectionState(): Observable<'disconnected' | 'connecting' | 'connected'> {
        return this.connectionState$.asObservable();
    }

    get isConnected(): boolean {
        return this.rxStomp?.connected() ?? false;
    }

    disconnect(): void {
        // Fix #3: Clean up subscriptions before disconnecting
        this.disconnect$.next();
        this.connectionSubscriptions.forEach((sub) => sub.unsubscribe());
        this.connectionSubscriptions = [];

        this.rxStomp?.deactivate();
        this.rxStomp = null;
        this.connectionState$.next('disconnected');
    }

    ngOnDestroy(): void {
        this.disconnect();
        this.disconnect$.complete();
    }
}
